import React from 'react';
import {
  FmToastContext,
  type FmToastApi,
  type FmToastOptions,
  type FmToastTone,
} from './FmToastContext';

interface FmToastRecord {
  id: string;
  tone: FmToastTone;
  content: React.ReactNode;
  durationMs: number;
  testId?: string;
}

export interface FmToastProviderProps {
  children: React.ReactNode;
  defaultDurationMs?: number;
  maxVisible?: number;
}

const DEFAULT_DURATION_MS = 3600;
const DEFAULT_MAX_VISIBLE = 5;

const TONE_LABEL: Record<FmToastTone, string> = {
  success: 'Success',
  warning: 'Warning',
  error: 'Error',
  info: 'Info',
};

const TONE_GLYPH: Record<FmToastTone, string> = {
  success: 'OK',
  warning: '!',
  error: '!',
  info: 'i',
};

export const FmToastProvider: React.FC<FmToastProviderProps> = ({
  children,
  defaultDurationMs = DEFAULT_DURATION_MS,
  maxVisible = DEFAULT_MAX_VISIBLE,
}) => {
  const [toasts, setToasts] = React.useState<FmToastRecord[]>([]);
  const nextIdRef = React.useRef(0);
  const removeToast = React.useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = React.useCallback((
    tone: FmToastTone,
    content: React.ReactNode,
    options: FmToastOptions = {},
  ) => {
    if (content === null || content === undefined || content === false || content === '') return;
    nextIdRef.current += 1;
    const id = `fm-toast-${nextIdRef.current}`;
    const durationMs = options.durationMs ?? defaultDurationMs;
    setToasts((current) => {
      const next = [
        ...current,
        {
          id,
          tone,
          content,
          durationMs,
          testId: options.testId,
        },
      ];
      return next.slice(-maxVisible);
    });
  }, [defaultDurationMs, maxVisible]);

  const api = React.useMemo<FmToastApi>(() => ({
    success: (content, options) => show('success', content, options),
    warning: (content, options) => show('warning', content, options),
    error: (content, options) => show('error', content, options),
    info: (content, options) => show('info', content, options),
  }), [show]);

  return (
    <FmToastContext.Provider value={api}>
      {children}
      <div className="fm-toast-region">
        {toasts.map((toast) => (
          <FmToastItem key={toast.id} toast={toast} onDismiss={removeToast} />
        ))}
      </div>
    </FmToastContext.Provider>
  );
};

function FmToastItem({
  toast,
  onDismiss,
}: {
  toast: FmToastRecord;
  onDismiss: (id: string) => void;
}) {
  const dismiss = React.useCallback(() => {
    onDismiss(toast.id);
  }, [onDismiss, toast.id]);

  React.useEffect(() => {
    if (toast.durationMs <= 0) return;
    const timeout = window.setTimeout(dismiss, toast.durationMs);
    return () => window.clearTimeout(timeout);
  }, [dismiss, toast.durationMs]);

  return (
    <div
      className={`fm-toast fm-toast--${toast.tone}`}
      data-testid={toast.testId ?? `fm-toast-${toast.tone}`}
    >
      <span className="fm-toast__icon" aria-hidden="true">
        {TONE_GLYPH[toast.tone]}
      </span>
      <div
        className="fm-toast__content"
        role={toast.tone === 'error' || toast.tone === 'warning' ? 'alert' : 'status'}
        aria-atomic="true"
      >
        <span className="fm-toast__sr-label">{TONE_LABEL[toast.tone]}: </span>
        {toast.content}
      </div>
      <button
        type="button"
        className="fm-toast__close"
        onClick={dismiss}
        aria-label="Dismiss notification"
      >
        x
      </button>
    </div>
  );
}

export default FmToastProvider;
